import React, { useState, useEffect, useRef, useCallback } from 'react'; 
import { MessageCircle, Send, Bot, ChevronUp, ChevronDown } from 'lucide-react';
import chatService from '../../services/chatService';

const generateGuestId = () => {
    return 'guest_' + Math.random().toString(36).substr(2, 9) + Date.now();
};

const ChatWidget = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [messages, setMessages] = useState([]);
    const [inputValue, setInputValue] = useState('');
    const [roomId, setRoomId] = useState(null);
    const [userInfo, setUserInfo] = useState({ type: '', id: '', name: '' });
    const [isConnected, setIsConnected] = useState(false);
    
    const [unreadCount, setUnreadCount] = useState(0);
    const isOpenRef = useRef(isOpen);
    const messagesEndRef = useRef(null);
    const textareaRef = useRef(null);

    useEffect(() => {
        isOpenRef.current = isOpen;
        if (isOpen) setUnreadCount(0);
    }, [isOpen]);

    const initGuest = () => {
        let gid = localStorage.getItem('guest_id');
        if (!gid) {
            gid = generateGuestId();
            localStorage.setItem('guest_id', gid);
        }
        setUserInfo({ type: 'GUEST', id: gid, name: 'Khách vãng lai' });
    };

    useEffect(() => {
        const userStr = localStorage.getItem('user_info');
        if (userStr) {
            try {
                const user = JSON.parse(userStr);
                const userId = user.accountId || user.id || user.userId; 
                if (userId) {
                    setUserInfo({
                        type: 'USER',
                        id: parseInt(userId, 10),
                        name: user.accountName || user.username || 'Khách hàng'
                    });
                } else { initGuest(); }
            } catch (e) { initGuest(); }
        } else { initGuest(); }
    }, []);

    const loadHistoryAndSubscribe = useCallback(async () => {
        if (!userInfo.id) return;
        try {
            let res = userInfo.type === 'USER' 
                ? await chatService.getUserHistory(userInfo.id) 
                : await chatService.getGuestHistory(userInfo.id);

            const history = Array.isArray(res) ? res : (res?.data || []);
            setMessages(history);

            if (history.length > 0) {
                const rId = history[0].roomId;
                setRoomId(rId);
                
                chatService.subscribeToRoom(rId, (newMessage) => {
                    setMessages(prev => {
                        const exists = prev.find(m => m.messageId === newMessage.messageId);
                        if (exists) return prev;
                        return [...prev, newMessage];
                    });
                    
                    if (!isOpenRef.current && newMessage.senderType !== userInfo.type) {
                        setUnreadCount(prev => prev + 1);
                    }
                });
            }
        } catch (error) {
            console.error("Lỗi lấy lịch sử chat:", error);
        }
    }, [userInfo.id, userInfo.type]);

    useEffect(() => {
        if (userInfo.id && !isConnected) {
            chatService.connect(
                () => {
                    setIsConnected(true);
                    loadHistoryAndSubscribe();
                },
                (error) => console.error("Lỗi kết nối Chat:", error)
            );
        }
    }, [userInfo.id, isConnected, loadHistoryAndSubscribe]);

    useEffect(() => {
        if (isOpen && isConnected) {
            loadHistoryAndSubscribe();
        }
    }, [isOpen, isConnected, loadHistoryAndSubscribe]);

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages, isOpen]);

    const handleSendMessage = async (e) => {
        if (e) e.preventDefault();
        if (!inputValue.trim() || !isConnected) return;

        const messageDto = {
            roomId: roomId, 
            senderType: userInfo.type,
            senderName: userInfo.name,
            content: inputValue.trim(),
            guestId: userInfo.type === 'GUEST' ? userInfo.id : null,
            accountId: userInfo.type === 'USER' ? userInfo.id : null
        };

        chatService.sendMessage(messageDto);
        setInputValue('');
        
        if (textareaRef.current) {
            textareaRef.current.style.height = 'auto';
        }

        if (!roomId) {
            const tempMsg = { 
                ...messageDto, 
                messageId: Date.now(), 
                timestamp: new Date().toISOString(),
                senderName: userInfo.name 
            };
            setMessages(prev => [...prev, tempMsg]);
            
            setTimeout(() => {
                loadHistoryAndSubscribe();
            }, 1500); 
        }
    };

    const handleInputChange = (e) => {
        setInputValue(e.target.value);
        if (textareaRef.current) {
            textareaRef.current.style.height = 'auto';
            textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 100)}px`;
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage();
        }
    };

    // 👉 ĐÃ SỬA GỌN LẠI HOÀN TOÀN LOGIC THỜI GIAN
    const formatDividerTime = (timeStr) => {
        if (!timeStr) return '';
        return new Date(timeStr).toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit' });
    };

    const formatTooltipTime = (timeStr) => {
        if (!timeStr) return '';
        const d = new Date(timeStr);
        return `${d.toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit' })}, ${d.toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit', year: 'numeric' })}`;
    };

    return (
        <>
            <style>
                {`
                .fb_dialog, .fb-page, .fb-customerchat { bottom: 85px !important; }
                .zalo-chat-widget, #zalo-vr, #zalo-chat-widget { bottom: 85px !important; }
                
                .chat-textarea::-webkit-scrollbar { width: 4px; }
                .chat-textarea::-webkit-scrollbar-track { background: transparent; }
                .chat-textarea::-webkit-scrollbar-thumb { background: #e5e7eb; border-radius: 4px; }
                .chat-textarea::-webkit-scrollbar-thumb:hover { background: #d1d5db; }
                `}
            </style>

            <div className="fixed bottom-0 right-2 md:right-4 z-[99999] flex flex-col items-end shadow-2xl rounded-t-xl font-sans">
                {isOpen ? (
                    <div className="bg-white w-[300px] sm:w-[340px] flex flex-col h-[420px] max-h-[75vh] rounded-t-xl overflow-hidden border border-gray-200 transition-all duration-300 shadow-[0_-4px_25px_rgba(255,85,0,0.15)]">
                        <div 
                            onClick={() => setIsOpen(false)} 
                            className="bg-tnc-orange cursor-pointer text-white px-3 py-2.5 flex items-center justify-between hover:bg-tnc-orange-hover transition-colors"
                        >
                            <div className="flex items-center gap-3">
                                <div className="relative">
                                    <div className="w-8 h-8 bg-white/30 rounded-full flex items-center justify-center">
                                        <Bot size={18} className="text-white" />
                                    </div>
                                    <span className={`absolute bottom-0 right-0 w-2.5 h-2.5 border-2 border-tnc-orange rounded-full ${isConnected ? 'bg-green-400' : 'bg-red-400'}`}></span>
                                </div>
                                <div>
                                    <h3 className="font-bold text-[13px] tracking-wide text-white">CSKH Thành Nam PC</h3>
                                    <p className="text-[10px] text-white/90 font-medium">
                                        {isConnected ? 'Đang hoạt động' : 'Đang kết nối...'}
                                    </p>
                                </div>
                            </div>
                            <ChevronDown size={20} className="text-white/90 hover:text-white" />
                        </div>

                        <div className="flex-1 overflow-y-auto p-4 bg-[#fdfcfb] flex flex-col gap-3">
                            {messages.length === 0 ? (
                                <div className="flex-1 flex flex-col items-center justify-center text-center opacity-50 mt-10">
                                    <MessageCircle size={40} className="mb-2 text-tnc-orange" />
                                    <p className="text-sm font-medium text-gray-500">Xin chào! Chúng tôi có thể giúp gì cho bạn?</p>
                                </div>
                            ) : (
                                messages.map((msg, idx) => {
                                    const isSelf = msg.senderType === userInfo.type;
                                    const isBot = ['BOT', 'STAFF', 'ADMIN'].includes(msg.senderType);
                                    
                                    const prevMsg = idx > 0 ? messages[idx - 1] : null;
                                    let showTimeDivider = false;
                                    
                                    if (!prevMsg) {
                                        showTimeDivider = true;
                                    } else {
                                        const currTime = new Date(msg.timestamp).getTime();
                                        const prevTime = new Date(prevMsg.timestamp).getTime();
                                        if (currTime - prevTime > 1800000) {
                                            showTimeDivider = true;
                                        }
                                    }

                                    return (
                                        <React.Fragment key={msg.messageId || idx}>
                                            {showTimeDivider && (
                                                <div className="w-full flex justify-center my-1.5">
                                                    <span className="text-[10px] font-medium text-gray-400">
                                                        {formatDividerTime(msg.timestamp)}
                                                    </span>
                                                </div>
                                            )}

                                            <div className={`flex w-full ${isSelf ? 'justify-end' : 'justify-start'}`}>
                                                <div className="relative group/bubble max-w-[85%]">
                                                    <div className={`px-3 py-2 text-[13px] shadow-sm flex flex-col relative z-20 ${
                                                        isSelf 
                                                        ? 'bg-tnc-orange text-white rounded-2xl rounded-tr-sm' 
                                                        : isBot 
                                                            ? 'bg-blue-50 border border-blue-100 text-blue-800 rounded-2xl rounded-tl-sm'
                                                            : 'bg-white border border-gray-200 text-gray-800 rounded-2xl rounded-tl-sm'
                                                    }`}>
                                                        {!isSelf && <div className={`text-[9px] font-bold mb-0.5 ${isBot ? 'text-blue-600' : 'text-gray-500'}`}>{msg.senderName}</div>} 
                                                        <p className="leading-relaxed whitespace-pre-wrap break-words">{msg.content}</p>
                                                    </div>

                                                    <div className={`absolute bottom-full mb-1 opacity-0 scale-95 group-hover/bubble:opacity-100 group-hover/bubble:scale-100 transition-all duration-200 text-[10px] font-medium text-gray-500 bg-white/95 backdrop-blur-sm px-2.5 py-1.5 rounded-md shadow-sm border border-gray-100 whitespace-nowrap pointer-events-none z-50 ${
                                                        isSelf ? 'right-0 origin-bottom-right' : 'left-0 origin-bottom-left'
                                                    }`}>
                                                        {formatTooltipTime(msg.timestamp)}
                                                    </div>
                                                </div>
                                            </div>
                                        </React.Fragment>
                                    );
                                })
                            )}
                            <div ref={messagesEndRef} />
                        </div>

                        <form onSubmit={handleSendMessage} className="p-2 bg-white border-t border-gray-200 flex items-end gap-2 shrink-0">
                            <textarea 
                                ref={textareaRef}
                                value={inputValue}
                                onChange={handleInputChange}
                                onKeyDown={handleKeyDown}
                                placeholder={isConnected ? "Nhập tin nhắn..." : "Đang kết nối..."}
                                disabled={!isConnected}
                                rows={1}
                                className="chat-textarea flex-1 bg-gray-100 border-transparent focus:bg-white focus:border-tnc-orange focus:ring-1 focus:ring-orange-200 rounded-2xl px-4 py-2.5 text-[13px] outline-none transition-all resize-none overflow-y-auto min-h-[40px] max-h-[100px] leading-relaxed"
                            />
                            <button 
                                type="submit" 
                                disabled={!inputValue.trim() || !isConnected}
                                className="w-9 h-9 mb-0.5 bg-tnc-orange text-white rounded-full flex items-center justify-center hover:bg-tnc-orange-hover transition-colors shrink-0 disabled:bg-gray-300"
                            >
                                <Send size={14} className="ml-0.5" />
                            </button>
                        </form>
                    </div>
                ) : (
                    <button 
                        onClick={() => setIsOpen(true)} 
                        className="bg-tnc-orange w-[220px] md:w-[240px] text-white px-3 py-2 flex items-center justify-between sm:rounded-t-xl hover:bg-tnc-orange-hover transition-colors shadow-[0_-4px_15px_rgba(255,85,0,0.3)] group"
                    >
                        <div className="flex items-center gap-2.5 font-bold text-[12px] md:text-[13px] tracking-wide text-white relative">
                            <div className="relative">
                                <MessageCircle size={16} className="text-white group-hover:scale-110 transition-transform" />
                                {unreadCount > 0 && (
                                    <span className="absolute -top-2 -right-2 bg-white text-tnc-orange text-[9px] font-extrabold w-4 h-4 flex items-center justify-center rounded-full shadow-sm animate-bounce">
                                        {unreadCount > 9 ? '9+' : unreadCount}
                                    </span>
                                )}
                            </div>
                            Chat với CSKH tại đây
                        </div>
                        <ChevronUp size={16} className="text-white/90 group-hover:text-white transition-colors" />
                    </button>
                )}
            </div>
        </>
    );
};

export default ChatWidget;